import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import FullScreenDialog from '../../../components/FullScreenDialog';
import { EMPTY_FILTERS, LAST_ACTIVITY_WINDOWS } from '../utils/clientFilters';

const SectionLabel = ({ children }) => (
  <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
    {children}
  </Typography>
);

const ToggleRow = ({ label, checked, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
    <Typography variant="body2">{label}</Typography>
    <Switch size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} />
  </Box>
);

const ClientsFilterPanel = ({
  open,
  onClose,
  filters,
  onApply,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(filters);

  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const patch = (partial) => setDraft((prev) => ({ ...prev, ...partial }));

  const toggleLastActivityWindow = (window) =>
    setDraft((prev) => ({
      ...prev,
      lastActivityWindow: prev.lastActivityWindow === window ? null : window,
    }));

  const handleClear = () => setDraft(EMPTY_FILTERS);

  const handleApply = () => {
    onApply(draft);
    onClose();
  };

  return (
    <FullScreenDialog
      open={open}
      onClose={onClose}
      title={t('clients.filters.title')}
      actions={
        <Stack direction="row" spacing={1.5} sx={{ width: '100%' }}>
          <Button fullWidth variant="outlined" size="large" onClick={handleClear}>
            {t('clients.filters.clearAll')}
          </Button>
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<CheckIcon />}
            onClick={handleApply}
          >
            {t('clients.filters.apply')}
          </Button>
        </Stack>
      }
    >
      <SectionLabel>{t('clients.filters.relationship')}</SectionLabel>
      <ToggleRow
        label={t('clients.filters.assignedToMe')}
        checked={draft.assignedToMe}
        onChange={(v) => patch({ assignedToMe: v })}
      />
      <ToggleRow
        label={t('clients.filters.hasActiveConversation')}
        checked={draft.hasActiveConversation}
        onChange={(v) => patch({ hasActiveConversation: v })}
      />

      <Divider sx={{ my: 3 }} />

      <SectionLabel>{t('clients.filters.lastActivity')}</SectionLabel>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        {LAST_ACTIVITY_WINDOWS.map((window) => {
          const selected = draft.lastActivityWindow === window;
          return (
            <Chip
              key={window}
              label={t(`clients.filters.lastActivity_${window}`)}
              size="medium"
              onClick={() => toggleLastActivityWindow(window)}
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              sx={{ height: 36 }}
            />
          );
        })}
      </Box>

      <Divider sx={{ my: 3 }} />

      <SectionLabel>{t('clients.filters.location')}</SectionLabel>
      <Stack direction="row" spacing={2}>
        <TextField
          fullWidth
          size="medium"
          label={t('clients.filters.city')}
          value={draft.city}
          onChange={(e) => patch({ city: e.target.value })}
        />
        <TextField
          fullWidth
          size="medium"
          label={t('clients.filters.state')}
          value={draft.state}
          onChange={(e) => patch({ state: e.target.value })}
        />
      </Stack>
    </FullScreenDialog>
  );
};

export default ClientsFilterPanel;
